import { useMutation, useQuery } from '@tanstack/react-query';
import { projectAssignApi } from '../lib/api.js';
import type { AssignProjectInput, AssignScopeSuggestion } from '../types/project-assign.js';

/**
 * 案件をアサインするミューテーションフック。
 *
 * 行ごとに成否が返る（全行失敗でも HTTP 200）ため、`isError` ではなく `data.results` を
 * 見て結果を出す。`onError` は認証・権限・ネットワークの失敗だけを拾う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサイン（要件定義 / 開発）を実行し、行ごとの結果を返す
 * @graph-connects projectAssignApi.assign [calls] 案件アサイン実行 API
 */
export function useAssignProject() {
  return useMutation({
    mutationFn: (input: AssignProjectInput) => projectAssignApi.assign(input),
  });
}

/**
 * 「作らない成果物」の AI 仮判断を取る（決めすぎないゲートの初期値）。
 *
 * **案件キーの並びをキーにして取り回す。** 貼り付けを打っている最中に何度も投げないよう、
 * 呼び手が確定した案件キーだけを渡す（モーダル側で入力を落ち着かせてから渡す）。
 * 案件ごとに AI を 1 回呼ぶので、同じ並びの再取得はキャッシュで止める。
 *
 * 失敗しても画面は使える（仮判断が無いだけ。人が自分で選べる）ので、リトライはしない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の憲章から「作らない成果物」を AI に仮判断させ、アサイン画面の初期値として取る
 * @graph-connects projectAssignApi.suggestScope [calls] 作らない成果物の仮判断 API
 */
export function useRequirementScopeSuggestions(issueKeys: readonly string[]) {
  return useQuery<AssignScopeSuggestion[]>({
    queryKey: [
      'project-assign',
      'scope-suggestions',
      [...issueKeys].sort((a, b) => a.localeCompare(b)),
    ],
    queryFn: async () =>
      (await projectAssignApi.suggestScope({ issueKeys: [...issueKeys] })).suggestions,
    enabled: issueKeys.length > 0,
    retry: false,
    staleTime: Infinity,
  });
}
